import { useMemo, useState } from 'react';
import PageBanner from '../components/ui/PageBanner';
import doctors from '../data/doctors.import.json';
import './Packages.css';

const SPECIALTIES = [
  {
    label: 'General Surgery',
    matches: (spec) => /general/i.test(spec) && /surgeon/i.test(spec) && !/bariatric/i.test(spec),
  },
  {
    label: 'Neuro Surgery',
    matches: (spec) => /neuro\s*surgeon|neurosurgeon/i.test(spec),
  },
  {
    label: 'Ortho Surgery',
    matches: (spec) => /ortho/i.test(spec),
  },
  {
    label: 'Plastic Surgery',
    matches: (spec) => /plastic/i.test(spec),
  },
  {
    label: 'Gynae Surgery',
    matches: (spec) => /gyn|gynea|gynaco|obstet|obster/i.test(spec),
  },
  {
    label: 'ENT Surgery',
    matches: (spec) => /\bent\b|otolar/i.test(spec),
  },
  {
    label: 'Pediatric Surgery',
    matches: (spec) => /(peads?|pediatric).*(surgeon|surgery)|(surgeon|surgery).*(peads?|pediatric)/i.test(spec),
  },
  {
    label: 'Gastro Entrology',
    matches: (spec) => /gastro/i.test(spec),
  },
  {
    label: 'Urology Surgery',
    matches: (spec) => /urolog|urog/i.test(spec),
  },
  {
    label: 'Bariatic Surgery',
    matches: (spec) => /bariatric|bariatic/i.test(spec),
  },
];

function getDoctorListName(doctor) {
  return String(doctor.name || '')
    .replace(/^(dr\.?|dr,|prof\.?|brig\.?|brigadier)\s*/i, '')
    .trim();
}

function getDoctorSpec(doctor) {
  return String(doctor.spec || '').replace(/\s+/g, ' ').trim();
}

function getSpecialty(label) {
  return SPECIALTIES.find((specialty) => specialty.label === label);
}

function doctorMatchesSpecialty(doctor, specialty) {
  return specialty.matches(getDoctorSpec(doctor));
}

export default function Packages({ setPage }) {
  const [activeCategory, setActiveCategory] = useState('');
  const [specialtySearch, setSpecialtySearch] = useState('');

  const filteredSpecialties = useMemo(() => {
    const query = specialtySearch.trim().toLowerCase();
    return SPECIALTIES.filter((specialty) => !query || specialty.label.toLowerCase().includes(query));
  }, [specialtySearch]);

  const sortedDoctors = useMemo(
    () => [...doctors].sort((a, b) => getDoctorListName(a).localeCompare(getDoctorListName(b))),
    []
  );

  const allowedDoctors = useMemo(
    () => sortedDoctors.filter((doctor) => SPECIALTIES.some((specialty) => doctorMatchesSpecialty(doctor, specialty))),
    [sortedDoctors]
  );

  const doctorsByCategory = useMemo(() => {
    if (!activeCategory) return [];
    const specialty = getSpecialty(activeCategory);
    if (!specialty) return [];
    return sortedDoctors.filter((doctor) => doctorMatchesSpecialty(doctor, specialty));
  }, [activeCategory, sortedDoctors]);

  const visibleDoctors = activeCategory ? doctorsByCategory : allowedDoctors;
  const doctorsTitle = activeCategory ? `${activeCategory} Doctors` : 'All Doctors';

  const clearDoctorsFilter = () => {
    setActiveCategory('');
  };

  return (
    <>
      <PageBanner title="Our Doctors" crumbs={['Home', 'Our Doctors']} />

      <section className="section">
        <div className="section-inner">
          <div className="pkg__layout">
            <aside className="pkg__sidebar pkg__sidebar--specialties">
              <h4 className="pkg__filter-title">Specialties</h4>
              <p className="pkg__cat-label">Find doctors by department</p>
              <input
                className="input pkg__search"
                type="search"
                value={specialtySearch}
                placeholder="Search specialties"
                onChange={(event) => setSpecialtySearch(event.target.value)}
              />
              <div className="pkg__alpha-filter" aria-label="Show all doctors">
                <button
                  type="button"
                  className={`pkg__alpha-btn ${!activeCategory ? 'pkg__alpha-btn--active' : ''}`}
                  onClick={clearDoctorsFilter}
                >
                  All
                </button>
              </div>
              <div className="pkg__spec-list">
                {filteredSpecialties.map((specialty) => (
                  <button
                    key={specialty.label}
                    type="button"
                    className={`pkg__spec-btn ${activeCategory === specialty.label ? 'pkg__spec-btn--active' : ''}`}
                    onClick={() => {
                      setActiveCategory(specialty.label);
                    }}
                  >
                    {specialty.label}
                  </button>
                ))}
                {!filteredSpecialties.length ? (
                  <p className="pkg__no-results">No specialties found.</p>
                ) : null}
              </div>
            </aside>

            <div className="pkg__content">
              <div className="pkg__head">
                <h3 className="pkg__grid-title">{doctorsTitle}</h3>
                <button type="button" className="btn-outline pkg__clear" onClick={clearDoctorsFilter}>
                  Show All Doctors
                </button>
              </div>

              {visibleDoctors.length ? (
                <div className="pkg__doctors-grid">
                  {visibleDoctors.map((doctor) => (
                    <div key={`${doctor.id}-${doctor.name}`} className="pkg__doctor-card card">
                      <div className="pkg__doctor-avatar">
                        {doctor.photo ? (
                          <img
                            className="pkg__doctor-avatar-img"
                            src={doctor.photo}
                            alt={doctor.name}
                            loading="lazy"
                          />
                        ) : (
                          <span>DR</span>
                        )}
                      </div>
                      <h4 className="pkg__doctor-name">{doctor.name}</h4>
                      <button
                        type="button"
                        className="btn-primary pkg__doctor-btn"
                        onClick={() => setPage?.('doctor-details', { params: { doctorId: doctor.id } })}
                      >
                        More Details
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="pkg__empty card">
                  <h3>No doctors found</h3>
                  <p>Choose another specialty from the side list.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
